import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useAdminChapters } from '@/presentation/hook/useAdminChapters';
import { useAdminSubcategories } from '@/presentation/hook/useAdminSubcategories';
import { Btn, Alert, Modal, InputField, MultilineText } from '@/presentation/component/ui';
import { inputClass } from '@/presentation/component/formStyles';
import { truncate } from '@/presentation/util/text';
import { AdminBreadcrumb } from '@/presentation/component/admin/AdminBreadcrumb';
import type { AdminChapter } from '@/domain/model/AdminChapter';

interface ChapterFormData {
  name: string;
  description: string;
  subcategoryId: string;
  displayOrder: number;
}

const EMPTY_FORM: ChapterFormData = {
  name: '',
  description: '',
  subcategoryId: '',
  displayOrder: 0,
};

export const AdminChaptersPage = () => {
  const { t } = useTranslation();
  const { subcategoryId: parentId } = useParams<{ subcategoryId: string }>();
  const navigate = useNavigate();
  const isDrillDown = !!parentId;

  const {
    chapters,
    isLoading,
    error,
    fetchChapters,
    createChapter,
    updateChapter,
    deleteChapter,
    clearError,
  } = useAdminChapters();
  const { subcategories, fetchSubcategories } = useAdminSubcategories();

  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingChapter, setEditingChapter] = useState<AdminChapter | null>(null);
  const [formData, setFormData] = useState<ChapterFormData>(EMPTY_FORM);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const activeFilter = isDrillDown ? parentId : selectedSubcategoryId;
  const parentSubcategory = isDrillDown ? subcategories.find((s) => s.id === parentId) : null;

  useEffect(() => {
    fetchSubcategories();
  }, [fetchSubcategories]);

  useEffect(() => {
    fetchChapters(activeFilter || undefined);
  }, [fetchChapters, activeFilter]);

  const handleSubcategoryFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubcategoryId(e.target.value);
  };

  const handleRowClick = (chapterId: string) => {
    navigate(`/admin/chapters/${chapterId}/courses`);
  };

  const openCreateForm = () => {
    setEditingChapter(null);
    setFormData({ ...EMPTY_FORM, ...(parentId ? { subcategoryId: parentId } : {}) });
    setIsFormOpen(true);
  };

  const openEditForm = (ch: AdminChapter) => {
    setEditingChapter(ch);
    setFormData({
      name: ch.name,
      description: ch.description,
      subcategoryId: ch.subcategoryId,
      displayOrder: ch.displayOrder,
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let success: boolean;
    if (editingChapter) {
      success = await updateChapter(editingChapter.id, formData);
      if (success) {
        toast.success(t('admin.chapterUpdated'));
      }
    } else {
      success = await createChapter(formData);
      if (success) {
        toast.success(t('admin.chapterCreated'));
      }
    }
    if (success) {
      setIsFormOpen(false);
      setEditingChapter(null);
      setFormData(EMPTY_FORM);
      fetchChapters(activeFilter || undefined);
    }
  };

  const handleConfirmDelete = async () => {
    if (deletingId) {
      const success = await deleteChapter(deletingId);
      if (success) {
        toast.success(t('admin.chapterDeleted'));
        fetchChapters(activeFilter || undefined);
      } else {
        toast.error(t('admin.deleteChapterConflict'));
      }
      setDeletingId(null);
    }
  };

  return (
    <div>
      {isDrillDown && (
        <AdminBreadcrumb
          items={[
            { label: t('admin.subcategoriesNav'), to: '/admin/subcategories' },
            { label: parentSubcategory?.name ?? '...' },
          ]}
        />
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-ink)]">{t('admin.chaptersNav')}</h1>
          <p className="text-sm text-[var(--color-ink-faint)]">
            {chapters.length} {t('admin.chapters')}
          </p>
        </div>
        <Btn kind="primary" icon={<Plus className="h-4 w-4" />} onClick={openCreateForm}>
          {t('admin.addChapter')}
        </Btn>
      </div>

      {!isDrillDown && (
        <div className="mt-4">
          <label htmlFor="subcategory-filter" className="sr-only">
            {t('admin.parentSubcategory')}
          </label>
          <select
            id="subcategory-filter"
            value={selectedSubcategoryId}
            onChange={handleSubcategoryFilter}
            className={`${inputClass()} sm:w-64`}
          >
            <option value="">{t('admin.allSubcategories')}</option>
            {subcategories.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <Alert kind="bad" onClose={clearError} className="mt-4">
          {t(error)}
        </Alert>
      )}

      {isLoading && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('common.loading')}</p>
      )}

      {!isLoading && chapters.length === 0 && !error && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('admin.noChapters')}</p>
      )}

      {chapters.length > 0 && (
        <>
          <div className="mt-6 hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line)] text-[var(--color-ink-faint)]">
                  <th className="pb-3 pr-4 font-medium">{t('admin.chapterName')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.chapterDescription')}</th>
                  {!isDrillDown && (
                    <th className="pb-3 pr-4 font-medium">{t('admin.parentSubcategory')}</th>
                  )}
                  <th className="pb-3 pr-4 font-medium">{t('admin.displayOrder')}</th>
                  <th className="pb-3 font-medium">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {chapters.map((ch: AdminChapter) => (
                  <tr
                    key={ch.id}
                    role="button"
                    tabIndex={0}
                    aria-label={t('admin.viewCourses')}
                    className="cursor-pointer border-b border-[var(--color-line)] text-[var(--color-ink)] transition-colors hover:bg-[var(--color-surface-2)]"
                    onClick={() => handleRowClick(ch.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(ch.id);
                      }
                    }}
                  >
                    <td className="py-3 pr-4 font-medium">{ch.name}</td>
                    <td className="py-3 pr-4">
                      <MultilineText className="text-[var(--color-ink-soft)]">
                        {truncate(ch.description, 60)}
                      </MultilineText>
                    </td>
                    {!isDrillDown && <td className="py-3 pr-4">{ch.subcategoryName}</td>}
                    <td className="py-3 pr-4">{ch.displayOrder}</td>
                    <td className="py-3">
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Btn
                          size="sm"
                          kind="ghost"
                          aria-label={t('admin.editChapter')}
                          onClick={() => openEditForm(ch)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Btn>
                        <Btn
                          size="sm"
                          kind="ghost"
                          aria-label={t('admin.deleteChapter')}
                          onClick={() => setDeletingId(ch.id)}
                        >
                          <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 space-y-3 md:hidden">
            {chapters.map((ch: AdminChapter) => (
              <div
                key={ch.id}
                role="button"
                tabIndex={0}
                aria-label={t('admin.viewCourses')}
                className="cursor-pointer rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-4 transition-colors hover:bg-[var(--color-surface-2)]"
                onClick={() => handleRowClick(ch.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(ch.id);
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[var(--color-ink)]">{ch.name}</p>
                    <MultilineText className="mt-1 text-sm text-[var(--color-ink-soft)]">
                      {ch.description}
                    </MultilineText>
                  </div>
                  <div className="ml-2 flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Btn
                      size="sm"
                      kind="ghost"
                      aria-label={t('admin.editChapter')}
                      onClick={() => openEditForm(ch)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Btn>
                    <Btn
                      size="sm"
                      kind="ghost"
                      aria-label={t('admin.deleteChapter')}
                      onClick={() => setDeletingId(ch.id)}
                    >
                      <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
                    </Btn>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-ink-faint)]">
                  {!isDrillDown && <span>{ch.subcategoryName}</span>}
                  <span>
                    {t('admin.displayOrder')}: {ch.displayOrder}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={editingChapter ? t('admin.editChapter') : t('admin.addChapter')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <InputField
            label={t('admin.chapterName')}
            placeholder={t('admin.chapterNamePlaceholder')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="chapter-description"
              className="text-sm font-medium text-[var(--color-ink-soft)]"
            >
              {t('admin.chapterDescription')}
            </label>
            <textarea
              id="chapter-description"
              className={inputClass()}
              placeholder={t('admin.chapterDescriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="chapter-subcategory"
              className="text-sm font-medium text-[var(--color-ink-soft)]"
            >
              {t('admin.parentSubcategory')} <span className="text-[var(--color-bad)]">*</span>
            </label>
            <select
              id="chapter-subcategory"
              value={formData.subcategoryId}
              onChange={(e) => setFormData({ ...formData, subcategoryId: e.target.value })}
              className={inputClass()}
              required
              disabled={isDrillDown && !editingChapter}
            >
              <option value="">{t('admin.parentSubcategory')}</option>
              {subcategories.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>
          <InputField
            label={t('admin.displayOrder')}
            type="number"
            min={0}
            value={formData.displayOrder}
            onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) })}
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <Btn kind="ghost" type="button" onClick={() => setIsFormOpen(false)}>
              {t('admin.cancel')}
            </Btn>
            <Btn kind="primary" type="submit" disabled={isLoading} isLoading={isLoading}>
              {t('admin.save')}
            </Btn>
          </div>
        </form>
      </Modal>

      <Modal
        open={deletingId !== null}
        onClose={() => setDeletingId(null)}
        title={t('admin.confirmDelete')}
        size="sm"
      >
        <p className="text-sm text-[var(--color-ink-soft)]">{t('admin.confirmDeleteChapter')}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Btn kind="ghost" onClick={() => setDeletingId(null)}>
            {t('admin.cancel')}
          </Btn>
          <Btn
            kind="primary"
            onClick={handleConfirmDelete}
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t('admin.deleteChapter')}
          </Btn>
        </div>
      </Modal>
    </div>
  );
};
